import { InfinityIcon } from "@images/svgIcons/infinityIcon"
import { OnOffIcon } from "@images/svgIcons/onOffIcon"
import { word } from "@modules/core/utils/i18n"
import { ProgramBundleType } from "@modules/programBundle/api/programBundleDto"
import { ProgramBundleItemEntity } from "@modules/programBundle/entities/programBundleEntity"
import { useProgramBundleSidebar } from "@modules/programBundle/hooks/useProgramBundleSidebar"
import { HUText } from "@modules/ui/components/huText"
import Line from "@modules/ui/components/line"
import { ChevronDown, ChevronRight, ChevronUp } from "lucide-react"
import { Skeleton } from "primereact/skeleton"
import { Tooltip } from "primereact/tooltip"
import { useState } from "react"
import { useTheme } from "styled-components"

type ProgramBundleListViewProps = {
  programBundles: ProgramBundleItemEntity[]
  isLoading: boolean
  emptyView: React.ReactNode
  titleObject?: {
    titleText: string
    titleColor: any
    titleBgColor: string
  }
  button?: React.ReactNode
} & React.HTMLAttributes<HTMLDivElement>

export const ProgramBundleListView: React.FC<ProgramBundleListViewProps> = ({
  programBundles,
  isLoading,
  titleObject,
  emptyView,
  button,
  ...props
}) => {
  const theme = useTheme()

  const classNameDefault = props.className ?? "flex flex-column mt-4"
  const NAME_MAX_LENGTH = 26
  const FOUNDER_MAX_LENGTH = 18

  const bundleHeaders = [
    { label: word("programBunlde.listView.name"), col: "col-5" },
    { label: word("programBunlde.listView.founder"), col: "col-3" },
    { label: word("programBunlde.listView.programs"), col: "col-2" },
    { label: word("programBunlde.listView.type"), col: "col-2" },
  ]

  const [isColapsed, setIsColapsed] = useState(false)
  const { navigateToProgramBundleResume } = useProgramBundleSidebar()

  return (
    <div className={classNameDefault} {...props}>
      {/* HEADER & CHEVRON */}
      <div className="flex justify-content-between align-items-center ">
        {titleObject ? (
          <HUText
            className={`w-min rounded-full px-3 py-2 ${titleObject.titleBgColor}`}
            fontStyle="TM"
            color={titleObject.titleColor}
          >
            {titleObject.titleText}
          </HUText>
        ) : (
          <div></div>
        )}
        <div className="flex cursor-pointer" onClick={() => setIsColapsed(!isColapsed)}>
          {isColapsed ? (
            <ChevronDown strokeWidth={2} color={theme.mainBlack} size={30} />
          ) : (
            <ChevronUp strokeWidth={2} color={theme.mainBlack} size={30} />
          )}
        </div>
      </div>

      {!isColapsed && !isLoading && (
        <>
          {/* EMPTY VIEW */}
          {programBundles.length === 0 ? (
            <div className="flex flex-column justify-content-center align-items-center mt-4">{emptyView}</div>
          ) : (
            <>
              {/* BUTTON ACTION */}
              {button ? <div className="flex justify-content-end mt-1">{button}</div> : null}
              {/* LIST */}
              <div className="flex flex-column w-full mt-4">
                <div className="grid mb-2">
                  {bundleHeaders.map((header) => (
                    <div className={header.col} key={`${header.label}_CREATED`}>
                      <HUText fontStyle="LM" color={theme.neutralColor600}>
                        {header.label}
                      </HUText>
                    </div>
                  ))}
                </div>
                <Line color={theme.neutralColor900} strokeWidth={1} className="m-0" />
                {programBundles.map((programBundle) => (
                  <div
                    className="flex flex-column cursor-pointer"
                    key={programBundle.id}
                    onClick={() =>
                      navigateToProgramBundleResume(programBundle.id, true, { replaceAllComponents: false })
                    }
                  >
                    <div className="flex flex-column pt-3 pb-2 align-content-end">
                      <div className="  grid ">
                        {/* COL NAME */}
                        <div
                          className={`col-5 ${programBundle.name.length > NAME_MAX_LENGTH ? `tooltip-name-${programBundle.id} cursor-pointer` : ""}`}
                          data-pr-showdelay={200}
                          data-pr-position="bottom"
                        >
                          {programBundle.name.length > NAME_MAX_LENGTH ? (
                            <Tooltip className="tooltip-container" target={`.tooltip-name-${programBundle.id}`}>
                              <div className="tooltip-content">
                                <HUText type="label" fontStyle="TS" style={{ textAlign: "left" }}>
                                  {programBundle.name}
                                </HUText>
                              </div>
                            </Tooltip>
                          ) : null}
                          <HUText style={{ overflowWrap: "break-word" }} fontStyle="TS" color={theme.neutralColor900}>
                            {programBundle.name.length > NAME_MAX_LENGTH
                              ? programBundle.name.substring(0, NAME_MAX_LENGTH - 3) + "..."
                              : programBundle.name}
                          </HUText>
                        </div>
                        {/* COL FOUNDER */}
                        <div
                          className={`col-3
                        ${
                          programBundle.owner.lastname.length + programBundle.owner.firstname.length >
                          FOUNDER_MAX_LENGTH
                            ? `tooltip-founder-${programBundle.id} cursor-pointer`
                            : ""
                        }`}
                          data-pr-showdelay={200}
                          data-pr-position="bottom"
                        >
                          {programBundle.owner.lastname.length + programBundle.owner.firstname.length >
                          FOUNDER_MAX_LENGTH ? (
                            <Tooltip className="tooltip-container" target={`.tooltip-founder-${programBundle.id}`}>
                              <div className="tooltip-content">
                                <HUText type="label" fontStyle="LS" style={{ textAlign: "left" }}>
                                  {programBundle.owner.firstname + " " + programBundle.owner.lastname}
                                </HUText>
                              </div>
                            </Tooltip>
                          ) : null}
                          <HUText style={{ overflowWrap: "break-word" }} fontStyle="LS" color={theme.neutralColor900}>
                            {programBundle.owner.lastname.length + programBundle.owner.firstname.length >
                            FOUNDER_MAX_LENGTH
                              ? (programBundle.owner.firstname + " " + programBundle.owner.lastname).substring(
                                  0,
                                  FOUNDER_MAX_LENGTH,
                                ) + "..."
                              : programBundle.owner.firstname + " " + programBundle.owner.lastname}
                          </HUText>
                        </div>
                        {/* COL PROGRAMS */}
                        <div className=" col-2">
                          <HUText fontStyle="BS" color={theme.neutralColor900}>
                            x {programBundle.nbPrograms}
                          </HUText>
                        </div>
                        {/* COL TYPE */}
                        <div className="col-1">
                          {programBundle.type === ProgramBundleType.VIRTUAL && (
                            <InfinityIcon color={theme.neutralColor900} width={24} height={24} />
                          )}
                          {programBundle.type === ProgramBundleType.ON_OFF && (
                            <OnOffIcon color={theme.neutralColor900} width={24} height={24} />
                          )}
                        </div>
                        <div className="col-1">
                          <ChevronRight color={theme.neutralColor900} strokeWidth={1} />
                        </div>
                      </div>
                    </div>
                    <Line color={theme.neutralColor300} strokeWidth={1} className="m-0" />
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {isLoading && (
        <div className="flex h-20rem mt-4">
          <Skeleton width="100%" height="100%" className="rounded-33" />
        </div>
      )}
    </div>
  )
}
